import { Button } from '@crazyegginc/hatch';
import classNames from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';

import { ReactComponent as ArrowIcon } from '@crazyegginc/hatch/dist/images/icon-arrow.svg';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { withQueryParams } from '../../utils';
import { useAuthContext, useMutation } from '/src/hooks';
import { upsertUserOnboardingMutation } from '/src/features/onboarding/mutations';
import { addSiteMutation } from '/src/features/options/mutations';
import { createSnapshotsMutation } from '/src/features/snapshots/mutations';
import { completeOnboarding } from '../../completion';
import { useOnboardingContext } from '../../hooks/useOnboardingContext';

export function Footer({
  children,
  canBack = false,
  canSkip = false,
  nextText = null,
  backTo,
  nextTo,
  backOnClick,
  nextOnClick,
  backUpserts = {},
  nextUpserts = {},
  nextProps = {},
}) {
  const hasBuiltInActions = Boolean(nextText);
  const footerRef = useRef(null);
  const [footerHeight, setFooterHeight] = useState(0);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const element = footerRef.current;

    if (!element) {
      return;
    }

    const updateFooterHeight = () => {
      setFooterHeight(element.getBoundingClientRect().height);
    };

    updateFooterHeight();

    const resizeObserver = new ResizeObserver(updateFooterHeight);
    resizeObserver.observe(element);
    window.addEventListener('resize', updateFooterHeight);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateFooterHeight);
    };
  }, [canBack, canSkip, children, nextText]);

  return (
    <>
      <div aria-hidden className="sm:hidden" style={{ height: footerHeight }} />
      <footer
        ref={footerRef}
        className="fixed bottom-0 left-0 right-0 flex w-full flex-wrap items-center justify-between gap-3 border-t border-mystic-500 bg-white-lilac-500 p-6 sm:relative sm:bg-transparent sm:pt-6 sm:pb-0 sm:pl-0 sm:pr-0"
      >
        {hasBuiltInActions ? (
          <>
            <div className="w-full sm:hidden">
              <div className="flex w-full items-center gap-3">
                {canBack ? (
                  <BackButton
                    onClick={backOnClick}
                    to={backTo}
                    upserts={backUpserts}
                    className="w-auto min-w-[50px] justify-center px-4"
                  />
                ) : null}
                <NextButton
                  onClick={nextOnClick}
                  to={nextTo}
                  upserts={nextUpserts}
                  className={classNames('justify-center', canBack ? 'flex-1' : 'w-full')}
                  {...nextProps}
                >
                  {nextText}
                </NextButton>
              </div>
              {canSkip ? <SkipButton className="mt-3 w-full justify-center" /> : null}
            </div>

            <div className="hidden w-full items-center justify-between sm:flex">
              <div>{canBack ? <BackButton onClick={backOnClick} to={backTo} upserts={backUpserts} /> : null}</div>
              <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-4">
                {canSkip ? <SkipButton /> : null}
                <NextButton onClick={nextOnClick} to={nextTo} upserts={nextUpserts} {...nextProps}>
                  {nextText}
                </NextButton>
              </div>
            </div>
          </>
        ) : (
          children
        )}
      </footer>
    </>
  );
}

export function BackButton({ onClick, to, upserts = {}, className }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleBack = () => {
    onClick?.();

    if (!to) {
      navigate(-1);
      return;
    }

    navigate(withQueryParams(to, searchParams.toString(), upserts));
  };

  return (
    <Button
      variant="secondary"
      onClick={handleBack}
      className={classNames(
        'h-[45px] sm:h-[50px] flex items-center gap-5 px-5 py-[14px] text-base font-bold leading-6',
        className,
      )}
    >
      <ArrowIcon className="w-4 h-4 fill-current rotate-180 text-dodger-blue-500" />
      <span className="hidden text-dodger-blue-500 sm:block">Back</span>
    </Button>
  );
}

export function NextButton({ children, onClick, to, upserts = {}, className, ...props }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleNext = () => {
    onClick?.();

    if (!to) {
      return;
    }

    navigate(withQueryParams(to, searchParams.toString(), upserts));
  };

  return (
    <Button
      size="xl"
      onClick={handleNext}
      className={classNames(
        'h-[45px] sm:h-[50px] flex items-center gap-5 px-5 py-[14px] text-base font-bold leading-6 w-full sm:w-auto',
        className,
      )}
      {...props}
    >
      <span>{children}</span>
      <ArrowIcon className="w-4 h-4 fill-current" />
    </Button>
  );
}

export function SkipButton({ className }) {
  const { currentUser } = useAuthContext();
  const navigate = useNavigate();
  const [isSkipping, setIsSkipping] = useState(false);
  const createSite = useMutation(addSiteMutation);
  const createSnapshots = useMutation(createSnapshotsMutation);
  const { mutateAsync: upsertUserOnboarding } = useMutation(upsertUserOnboardingMutation);
  const { flow, isHeatmapFlow, completionUrl } = useOnboardingContext(currentUser.onboarding.flow);

  const handleSkip = useCallback(async () => {
    if (isSkipping) {
      return;
    }

    setIsSkipping(true);
    await completeOnboarding({
      mode: 'skip',
      completionUrl,
      isHeatmapFlow,
      flow,
      completedAt: currentUser.onboarding.completedAt,
      createSite,
      createSnapshots,
      upsertUserOnboarding,
      navigate,
    });
    setIsSkipping(false);
  }, [
    completionUrl,
    createSite,
    createSnapshots,
    currentUser.onboarding.completedAt,
    flow,
    isHeatmapFlow,
    isSkipping,
    navigate,
    upsertUserOnboarding,
  ]);

  return (
    <Button
      size="xl"
      onClick={handleSkip}
      disabled={isSkipping}
      className={classNames(
        'h-[45px] sm:h-[50px] flex items-center gap-5 px-5 py-[14px] text-base font-bold leading-6 border-none bg-transparent hover:bg-transparent text-dodger-blue-500 hover:text-dodger-blue-700',
        className,
      )}
    >
      {isSkipping ? 'Skipping...' : 'Skip'}
    </Button>
  );
}
